import React from "react";
import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { ShieldCheck } from "lucide-react";

const SECTIONS = [
  {
    h: "Who we are",
    p: ["CivicHearth is a UK-based volunteer-matching platform. This notice explains what personal data we collect, why, and the rights you have under UK GDPR and the Data Protection Act 2018."],
  },
  {
    h: "What we collect and why",
    list: [
      "Account details (name, email, profile photo) — to run your account and connect you with organisations. Lawful basis: contract.",
      "Location (town/city or postcode you choose to pin) — to match you with nearby opportunities and, only if you enable it, send local alerts. Lawful basis: consent. Your precise coordinates are never shown publicly.",
      "Values, skills and bio — to personalise matches. Lawful basis: contract.",
      "Volunteering activity (applications, approved hours, badges) — to keep your record and issue certificates. Lawful basis: contract / legitimate interest.",
      "Content you post (stories, messages, group chat) — visible as described where you post it. Lawful basis: consent.",
    ],
  },
  {
    h: "What we never share publicly",
    list: [
      "Your email address — never shown to other users.",
      "Your postcode or precise coordinates — public pages show your town or city at most.",
      "Leaderboards show only your first name and city, and you can opt out entirely.",
      "You can make your whole profile private in your profile settings.",
    ],
  },
  {
    h: "Your rights",
    list: [
      "Access & portability — download a full copy of your data from your profile settings at any time.",
      "Erasure — delete your account from profile settings. Your personal data is removed; records other people rely on (e.g. an organisation's approved hour logs, messages you sent them) are anonymised to \"Deleted user\".",
      "Rectification — edit your profile at any time.",
      "Objection & consent withdrawal — turn off local alert emails and all email notifications in profile settings.",
      "You may also complain to the Information Commissioner's Office (ico.org.uk).",
    ],
  },
  {
    h: "Retention & security",
    list: [
      "Sessions expire after 7 days.",
      "Data is retained while your account is active and removed or anonymised on deletion.",
      "Access to admin tooling is restricted, and moderation actions are logged.",
    ],
  },
];

export default function Privacy() {
  return (
    <div className="max-w-3xl mx-auto px-6 py-14" data-testid="privacy-page">
      <Seo title="Privacy" description="How CivicHearth collects, uses and protects your personal data under UK GDPR." />
      <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold flex items-center gap-1.5">
        <ShieldCheck className="w-4 h-4 text-terracotta" /> UK GDPR
      </div>
      <h1 className="font-display text-4xl sm:text-5xl text-forest mt-3 tracking-tight">Privacy notice</h1>
      <p className="text-neutral-600 mt-3">Last updated {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>

      <div className="mt-10 space-y-10">
        {SECTIONS.map(s => (
          <section key={s.h}>
            <h2 className="font-display text-2xl text-forest tracking-tight">{s.h}</h2>
            {s.p?.map((t) => <p key={t} className="text-neutral-700 leading-relaxed mt-3">{t}</p>)}
            {s.list && (
              <ul className="mt-3 space-y-2">
                {s.list.map((t, i) => (
                  <li key={t} className="text-neutral-700 leading-relaxed pl-5 relative">
                    <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-terracotta" />
                    {t}
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      <div className="mt-12 bg-forest/5 border border-forest/10 rounded-2xl p-6">
        <div className="font-display text-xl text-forest">Manage your data now</div>
        <p className="text-sm text-neutral-600 mt-2">
          Privacy toggles, data download and account deletion all live in{" "}
          <Link to="/onboarding" className="text-forest underline font-medium">your profile settings</Link>.
        </p>
      </div>
    </div>
  );
}
